import { Award, Users, Handshake, Zap, Target, Sparkles, Eye, Compass, ShieldCheck, GraduationCap, Briefcase, Quote } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import BridgeVisual from "@/components/site/BridgeVisual";
import { useSEO } from "@/lib/seo";
import { useSiteSettings } from "@/lib/settings";
import { ABOUT_DEFAULTS, pick, renderInlineBold } from "@/lib/cms-defaults";

// H2 title size class — bumped by ~2px from previous text-3xl / sm:text-4xl
const H2_TITLE = "font-heading text-[32px] font-semibold text-slate-900 sm:text-[38px] leading-tight";

const WHY_US_ICONS = [Award, Users, Handshake, Zap, Target, Sparkles];

export default function About() {
  const { settings } = useSiteSettings();
  const c = settings?.about_content || {};
  const t = (k) => pick(c, ABOUT_DEFAULTS, k);

  useSEO({
    title: "About Us",
    description: t("hero_body"),
    image: t("hero_image"),
    pageKey: "about",
  });

  const storyParagraphs = String(t("story_body") || "").split(/\n\n+/).filter(Boolean);
  const founderBioParagraphs = String(t("founder_bio") || "").split(/\n\n+/).filter(Boolean);
  const founderInitials = String(t("founder_name") || "AS").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "AS";
  const founderBadges = String(t("founder_badges") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const BADGE_ICONS = [Briefcase, ShieldCheck, GraduationCap];

  const whyUs = Array.from({ length: 6 }, (_, i) => ({
    icon: WHY_US_ICONS[i],
    title: t(`why_us_${i + 1}_title`),
    tagline: t(`why_us_${i + 1}_tagline`),
    text: t(`why_us_${i + 1}_text`),
  }));

  return (
    <>
      <PageHero
        overline={t("hero_overline")}
        title={t("hero_title")}
        description={t("hero_body")}
        image={t("hero_image")}
      />

      <section className="section-pad">
        <div className="container-page grid gap-12 md:grid-cols-12">
          <Reveal variant="left" className="md:col-span-7">
            <div className="overline mb-3">{t("story_overline")}</div>
            <h2 className={H2_TITLE}>{t("story_title")}</h2>
            <div className="prose-pharma mt-6 max-w-2xl">
              {storyParagraphs.map((p, i) => <p key={i}>{renderInlineBold(p)}</p>)}
            </div>
          </Reveal>
          <Reveal variant="right" delay={150} className="md:col-span-5">
            <BridgeVisual />
          </Reveal>
        </div>
      </section>

      {/* VISION · MISSION · PROMISE */}
      <section className="section-pad bg-slate-50">
        <div className="container-page">
          <Reveal className="mx-auto max-w-2xl text-center">
            <div className="overline mb-3">{t("drives_overline")}</div>
            <h2 className={H2_TITLE}>{t("drives_title")}</h2>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              { icon: Eye, overline: "Our Vision", title: t("vision_title"), body: t("vision_body"), accent: "text-primary bg-primary/10" },
              { icon: Compass, overline: "Our Mission", title: t("mission_title"), body: t("mission_body"), accent: "text-secondary bg-secondary/15" },
              { icon: ShieldCheck, overline: "Our Promise", title: t("promise_title"), body: t("promise_body"), accent: "text-primary bg-primary/10" },
            ].map(({ icon: Icon, overline, title, body, accent }, i) => (
              <Reveal
                key={overline}
                variant="up"
                delay={i * 120}
                data-testid={`about-pillar-${overline.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 hover-lift hover:border-primary/40 hover:shadow-lg"
              >
                <div className={`mb-5 inline-grid h-14 w-14 place-items-center rounded-xl ${accent} transition-transform group-hover:scale-110`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="overline mb-2">{overline}</div>
                <h3 className="font-heading text-lg font-semibold leading-snug text-slate-900">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET OUR FOUNDER */}
      <section className="section-pad bg-white">
        <div className="container-page grid gap-12 md:grid-cols-12 md:items-center">
          <Reveal variant="left" className="md:col-span-5">
            <div className="relative">
              <div className="absolute -left-3 -top-3 hidden h-24 w-24 rounded-2xl bg-secondary/15 md:block" />
              <div className="absolute -bottom-3 -right-3 hidden h-32 w-32 rounded-2xl bg-primary/10 md:block" />
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                {t("founder_image") ? (
                  <img src={t("founder_image")} alt={t("founder_name")} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="grid h-full w-full place-items-center bg-gradient-to-br from-primary/10 via-white to-secondary/15">
                    <div className="text-center">
                      <div className="mx-auto grid h-32 w-32 place-items-center rounded-full bg-primary text-primary-foreground">
                        <span className="font-heading text-5xl font-bold">{founderInitials}</span>
                      </div>
                      <div className="mt-5 font-heading text-2xl font-bold text-slate-900">{t("founder_name")}</div>
                      <div className="mt-1 text-sm text-slate-600">{t("founder_role")}</div>
                      {t("founder_credential") && (
                        <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-secondary/15 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-secondary">
                          <GraduationCap className="h-3 w-3" /> {t("founder_credential")}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          <div className="md:col-span-7">
            <Reveal variant="up">
              <div className="overline mb-3">{t("founder_overline")}</div>
              <h2 className={H2_TITLE}>{t("founder_name")}</h2>
              <div className="mt-2 text-sm font-semibold text-secondary">{t("founder_role")}</div>
            </Reveal>

            <Reveal variant="up" delay={100} className="prose-pharma mt-5 max-w-2xl">
              {founderBioParagraphs.map((p, i) => <p key={i}>{renderInlineBold(p)}</p>)}
            </Reveal>

            {founderBadges.length > 0 && (
              <Reveal variant="up" delay={200} className="mt-6 flex flex-wrap gap-3">
                {founderBadges.map((label, i) => {
                  const Icon = BADGE_ICONS[i % BADGE_ICONS.length];
                  return (
                    <div
                      key={label}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700"
                      data-testid={`founder-badge-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      <Icon className="h-3.5 w-3.5 text-primary" /> {label}
                    </div>
                  );
                })}
              </Reveal>
            )}

            {t("founder_quote") && (
              <Reveal variant="up" delay={300} className="mt-8 rounded-xl border border-primary/20 bg-primary/5 p-5">
                <Quote className="h-6 w-6 text-primary" />
                <p className="mt-2 font-heading text-lg italic leading-relaxed text-slate-800">{t("founder_quote")}</p>
                <div className="mt-3 text-xs font-semibold uppercase tracking-widest text-slate-500">— {t("founder_name")}, Founder</div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="section-pad bg-slate-50">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <div className="overline mb-3">{t("why_us_overline")}</div>
            <h2 className={H2_TITLE}>{t("why_us_title")}</h2>
            <p className="mt-4 text-slate-600">{t("why_us_body")}</p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {whyUs.map(({ icon: Icon, title, tagline, text }, i) => (
              <Reveal
                key={i}
                variant="up"
                delay={i * 100}
                data-testid={`why-us-${i + 1}`}
                className="group relative rounded-xl border border-slate-200 bg-white p-6 hover-lift hover:border-primary/40 hover:shadow-md"
              >
                <div className="absolute right-5 top-5 font-heading text-4xl font-bold text-slate-100 transition-colors group-hover:text-primary/10">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mb-4 inline-grid h-12 w-12 place-items-center rounded-lg bg-primary/10 text-primary transition-transform group-hover:scale-110">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
                <div className="mt-1 text-sm font-medium text-secondary">{tagline}</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
